import _ from 'lodash';
import { useMemo } from 'react';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import moment from 'moment';

const parseFormDate = (value) =>
  moment.isMoment(value)
    ? value
    : moment(value, ['MM/DD/YYYY', moment.ISO_8601]);

// An end date before the start date is never a valid range. Either date still
// missing is left to the required rule.
export const isRangeEndValid = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return true;
  }
  const start = parseFormDate(dateFrom);
  const end = parseFormDate(dateTo);
  if (!start.isValid() || !end.isValid()) {
    return true;
  }
  return !end.isBefore(start, 'day');
};

export const useFields = ({
  configuration,
  propertyOptions,
  propertyGroupOptions = [],
  userPropertyGroups = [],
  selectedProperties = [],
}) => {
  const hasFormatConfiguration =
    configuration?.formats && configuration.formats.length > 0;
  const hasPropertyFilter = configuration?.propertyFilter === true;
  const hasPropertyGroupsFilter = configuration?.propertyGroupsFilter === true;
  const hasExportOptions =
    configuration?.exportOptions && configuration.exportOptions.length > 0;
  const rawTimeSelectionOptions = useMemo(
    () => configuration?.timeSelectionOptions ?? [],
    [configuration?.timeSelectionOptions],
  );
  const hasTimeSelection = rawTimeSelectionOptions.length > 0;
  const hasMultipleTimeSelectionOptions = rawTimeSelectionOptions.length > 1;
  const supportsRange = rawTimeSelectionOptions
    .map((opt) => String(opt).toUpperCase())
    .includes('RANGE');
  const supportsYear = rawTimeSelectionOptions
    .map((opt) => String(opt).toUpperCase())
    .includes('YEAR');
  const supportsDate = rawTimeSelectionOptions
    .map((opt) => String(opt).toUpperCase())
    .includes('DATE');

  const formatOptions = useMemo(() => {
    if (!hasFormatConfiguration) {
      return [];
    }

    return configuration.formats.map((fmt) => {
      if (_.isString(fmt)) {
        const formatUpper = fmt.toUpperCase();
        if (formatUpper === 'CSV') {
          return { text: 'CSV', value: 'csv', disabled: false };
        } else if (formatUpper === 'EXCEL') {
          return { text: 'Excel', value: 'xlsx', disabled: false };
        }
        return { text: fmt, value: fmt.toLowerCase(), disabled: false };
      } else {
        // can use an object to directly set props
        return fmt;
      }
    });
  }, [hasFormatConfiguration, configuration?.formats]);

  const exportAsOptions = useMemo(() => {
    if (!hasExportOptions) {
      return [];
    }

    return configuration.exportOptions.map((option) => {
      const optionUpper = option.toUpperCase();
      if (optionUpper === 'GROUPED') {
        return { label: 'Grouped (one file)', value: 'grouped', size: 'small' };
      } else if (optionUpper === 'INDIVIDUAL') {
        return {
          label: 'Individual files',
          value: 'individual',
          size: 'small',
        };
      }
      return { label: option, value: option.toLowerCase(), size: 'small' };
    });
  }, [hasExportOptions, configuration?.exportOptions]);

  const timeSelectionOptions = useMemo(() => {
    if (!hasTimeSelection) {
      return [];
    }

    return rawTimeSelectionOptions.map((option) => {
      const optionUpper = option.toUpperCase();
      const labelMap = {
        RANGE: 'Range',
        YEAR: 'Year',
        DATE: 'Date',
      };
      return {
        label: labelMap[optionUpper] || option,
        value: optionUpper.toLowerCase(),
      };
    });
  }, [hasTimeSelection, rawTimeSelectionOptions]);

  const fields = useMemo(() => {
    const fieldsArray = [];
    // Guidance a report gives before anything is filled in, at the top of the form.
    if (configuration?.infoBanner) {
      fieldsArray.push({
        name: 'infoBanner',
        variant: 'banner',
        status: 'info',
        message: configuration.infoBanner,
        GridProps: { xs: 12 },
      });
    }
    const shouldDisableExportAs =
      !selectedProperties || selectedProperties.length <= 1;

    if (hasTimeSelection && hasMultipleTimeSelectionOptions) {
      fieldsArray.push({
        name: 'timeSelection',
        label: 'Time Selection',
        variant: 'radioGroup',
        options: timeSelectionOptions,
        row: true,
        rules: {
          required: true,
        },
        sx: {
          '&.MuiFormGroup-root': {
            flexDirection: 'row !important',
            gap: '16px',
          },
        },
        GridProps: {
          xs: 12,
        },
      });
    }

    if (hasTimeSelection && supportsYear) {
      // A report can rename the year field (yearProps), so the message follows
      // whatever label the field ends up with.
      const yearLabel = configuration?.yearProps?.label ?? 'Year';
      fieldsArray.push({
        name: 'year',
        label: yearLabel,
        variant: 'date',
        openTo: 'year',
        views: ['year'],
        maxDate: moment().endOf('year'),
        placeholder: 'YYYY',
        pattern: '\\d{4}',
        helperText: 'Required',
        // Theme paints Select placeholders with text.disabled (MuiSelect override); native input placeholders differ.
        sx: {
          '& .MuiOutlinedInput-input::placeholder': {
            color: 'text.disabled',
            opacity: 1,
          },
        },
        hidden: ({ getValues }) => {
          const timeSelection = getValues('timeSelection');
          return timeSelection !== 'year';
        },
        rules: {
          required: { value: true, message: `${yearLabel} is required` },
        },
        GridProps: {
          xs: 12,
          sm: 12,
        },
        ...configuration?.yearProps,
      });
    }

    if (hasTimeSelection && supportsDate) {
      fieldsArray.push({
        name: 'date',
        label: 'Date',
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        helperText: 'Required',
        hidden: ({ getValues }) => {
          const timeSelection = getValues('timeSelection');
          return timeSelection !== 'date';
        },
        rules: {
          required: { value: true, message: 'Date is required' },
        },
        GridProps: {
          xs: 12,
          sm: 12,
        },
        ...configuration?.dateProps,
      });
    }

    if (hasTimeSelection && supportsRange) {
      // A report can rename the range fields (dateFromProps / dateToProps), so
      // the messages follow whatever label the field ends up with.
      const dateFromLabel = configuration?.dateFromProps?.label ?? 'Date From';
      const dateToLabel = configuration?.dateToProps?.label ?? 'Date To';
      const isRangeHidden = ({ getValues }) => {
        const timeSelection = getValues('timeSelection');
        return timeSelection !== 'range';
      };

      fieldsArray.push({
        name: 'dateFrom',
        label: dateFromLabel,
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        helperText: 'Required',
        hidden: isRangeHidden,
        rules: {
          required: { value: true, message: `${dateFromLabel} is required` },
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
        ...configuration?.dateFromProps,
      });

      fieldsArray.push({
        name: 'dateTo',
        label: dateToLabel,
        variant: 'date',
        placeholder: 'MM/DD/YYYY',
        helperText: 'Required',
        hidden: isRangeHidden,
        rules: {
          required: { value: true, message: `${dateToLabel} is required` },
          // The component library's Form calls validate(value, getValues);
          // react-hook-form itself passes the form values instead.
          validate: (value, getValuesOrValues) => {
            const dateFrom =
              typeof getValuesOrValues === 'function'
                ? getValuesOrValues('dateFrom')
                : getValuesOrValues?.dateFrom;
            return (
              isRangeEndValid(dateFrom, value) ||
              `${dateToLabel} must be on or after ${dateFromLabel}`
            );
          },
        },
        GridProps: {
          xs: 12,
          sm: 6,
        },
        ...configuration?.dateToProps,
      });
    }

    const shouldShowPropertiesSection =
      hasPropertyGroupsFilter ||
      hasPropertyFilter ||
      hasFormatConfiguration ||
      hasExportOptions;

    if (shouldShowPropertiesSection) {
      fieldsArray.push({
        name: 'propertiesSectionDivider',
        variant: 'custom',
        component: (
          <Typography variant="titleSmall" color="text.secondary">
            Properties and format
          </Typography>
        ),
        GridProps: {
          xs: 12,
        },
      });
    }

    if (hasPropertyGroupsFilter) {
      const hasPropertyGroups = propertyGroupOptions.length > 0;

      fieldsArray.push({
        name: 'propertyGroup',
        label: 'Property Group',
        variant: 'singleSelect',
        options: propertyGroupOptions,
        placeholder: hasPropertyGroups
          ? 'Select property group...'
          : 'No property groups available',
        disabled: !hasPropertyGroups,
        GridProps: {
          xs: 12,
          sm: hasPropertyFilter ? 6 : 12,
        },
      });
    }

    if (hasPropertyFilter) {
      fieldsArray.push({
        name: 'properties',
        label: 'Properties',
        // explicit check for false for back compatibility
        variant:
          configuration?.multipleProperties !== false
            ? 'multiSelect'
            : 'singleSelect',
        options: propertyOptions,
        placeholder: 'Select properties...',
        selectAllOption: true,
        rules: {
          required: true,
        },
        GridProps: {
          xs: 12,
          sm: hasPropertyGroupsFilter ? 6 : 12,
        },
      });
    }

    if (hasFormatConfiguration) {
      fieldsArray.push({
        name: 'format',
        label: 'Format',
        variant: 'singleSelect',
        options: formatOptions,
        rules: {
          required: true,
        },
        GridProps: {
          xs: 12,
          sm: hasExportOptions ? 6 : 12,
        },
        ...configuration?.formatProps,
      });
    }

    if (hasExportOptions) {
      fieldsArray.push({
        name: 'exportAs',
        label: 'Export As',
        variant: 'radioGroup',
        options: exportAsOptions,
        row: true,
        disabled: shouldDisableExportAs,
        helperText: 'Required',
        rules: {
          required: true,
        },
        GridProps: {
          xs: 12,
          sm: hasFormatConfiguration ? 6 : 12,
        },
        ...configuration?.exportAsProps,
      });
    }

    return fieldsArray;
  }, [
    configuration,
    propertyOptions,
    propertyGroupOptions,
    selectedProperties,
    hasFormatConfiguration,
    hasPropertyFilter,
    hasPropertyGroupsFilter,
    hasExportOptions,
    hasTimeSelection,
    formatOptions,
    exportAsOptions,
    timeSelectionOptions,
    hasMultipleTimeSelectionOptions,
    supportsRange,
    supportsYear,
    supportsDate,
  ]);

  return fields;
};
